import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Header from "../../components/header";
import SeoHead from "../../components/seoHead";
import { Stores } from "../../dataLists/index";
import { LocalizedLink, useAppLocale, useLocalePath } from "../../i18n/LocaleRouting";
import { localizeProductEntry } from "../../i18n/productCopy";
import {
  SITE_NAME,
  SITE_URL,
  absoluteLocalizedUrl,
} from "../../i18n/seoUrls";
import { stripLocalePrefix } from "../../i18n/locales";
import {
  getProductInformationBySlug,
  heroBackgroundStyle,
  PRODUCT_ACCENT_CLASSES,
} from "./productInformationData";
import Footer from "../../components/footer";

interface ProductInformationProps {
  forcedProductSlug?: string;
  /** Explicit canonical path for TCG landings (avoids relying on window during prerender). */
  canonicalPath?: string;
}

/** Mobile (< md): thumbnails on the left, large preview on the right. */
function MobileThumbPreviewGallery({
  images,
  alt,
  thumbBorderClass,
}: {
  images: string[];
  alt: string;
  thumbBorderClass: string;
}) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
  }, [images]);

  if (images.length === 0) return null;

  const activeImage = images[activeIndex] ?? images[0];

  return (
    <div className="md:hidden w-full mt-6 px-4">
      <div className="flex items-center gap-3">
        <div className="flex flex-col gap-2 shrink-0 w-[54px]">
          {images.map((image, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={image}
                type="button"
                onClick={() => setActiveIndex(index)}
                aria-label={`Show card ${index + 1}`}
                aria-current={isActive ? "true" : undefined}
                className={`product-info-gallery-item appearance-none bg-transparent p-[3px] rounded-md overflow-hidden border-[1px] transition-colors ${
                  isActive ? thumbBorderClass : "border-white/20 opacity-75"
                }`}
                style={{ animationDelay: `${index * 0.08}s` }}
              >
                <img
                  src={image}
                  alt=""
                  draggable={false}
                  className="w-full h-auto object-contain"
                />
              </button>
            );
          })}
        </div>

        <div
          key={activeImage}
          className="product-info-gallery-item flex-1 min-w-0 flex items-center justify-center"
        >
          <img
            src={activeImage}
            alt={alt}
            draggable={false}
            className="product-info-gallery-image w-full h-auto max-h-[40vh] object-contain"
          />
        </div>
      </div>
    </div>
  );
}

export default function ProductInformation({
  forcedProductSlug,
  canonicalPath,
}: ProductInformationProps) {
  const { t } = useTranslation();
  const locale = useAppLocale();
  const localePath = useLocalePath();
  const { productTitle, slug } = useParams<{
    productTitle?: string;
    slug?: string;
  }>();
  const location = useLocation();
  const productKey = forcedProductSlug ?? productTitle ?? slug;
  const product = useMemo(() => {
    const base = getProductInformationBySlug(productKey);
    return base ? localizeProductEntry(base, locale) : undefined;
  }, [productKey, locale]);

  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const [locationError, setLocationError] = useState("");
  const accentClasses = product
    ? PRODUCT_ACCENT_CLASSES[product.accent]
    : PRODUCT_ACCENT_CLASSES.red;

  const heroStyle = product
    ? heroBackgroundStyle(product.heroImageUrl)
    : heroBackgroundStyle("");

  const locationInputId = `product-info-location-${product?.id ?? "unknown"}`;
  const currentPath = canonicalPath ?? location.pathname;
  const bareProductPath = stripLocalePrefix(currentPath) || "/";
  const productUrl = absoluteLocalizedUrl(bareProductPath, locale);
  const homeUrl = absoluteLocalizedUrl("/", locale);
  const productTitleText = product
    ? `${product.h1Line1} ${product.h1Accent}`.trim()
    : "";

  const knownPrefectures = useMemo(() => {
    const prefectures = new Set<string>();
    for (const item of Stores) {
      if (item.prefecture) prefectures.add(item.prefecture.toLowerCase());
    }
    return prefectures;
  }, []);

  const handleLocationSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed) {
      setLocationError(t("product.locationError"));
      return;
    }
    setLocationError("");
    const normalizedInput = trimmed.toLowerCase().replace(/-/g, " ");
    if (knownPrefectures.has(normalizedInput)) {
      navigate(
        localePath(
          `/regionalstore/${trimmed.toLowerCase().replace(/\s+/g, "-")}`,
        ),
      );
    } else {
      navigate(localePath("/regionalstore"));
    }
  };

  if (!product) {
    return (
      <>
        <SeoHead
          title={t("seo.notFound.title")}
          description={t("seo.notFound.description")}
          path={bareProductPath}
          noindex
        />
        <Header />
        <div className="min-h-screen bg-slate-900 text-slate-300 flex flex-col items-center justify-center gap-4 px-6">
          <h1 className="text-3xl font-bold text-white mb-2">
            {t("common.notFound")}
          </h1>
          <p className="text-slate-400 mb-6 text-center max-w-md text-lg">
            The product name in the URL does not exist or is not yet available.
            Please click on the Banner on the home page, or check if the link is
            correct.
          </p>
          <LocalizedLink
            to="/"
            className="text-red-400 hover:text-red-300 underline font-medium text-lg"
          >
            {t("common.backHome")}
          </LocalizedLink>
        </div>
      </>
    );
  }

  const metaDescription = product.sectionBody
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 160);

  return (
    <>
      <SeoHead
        title={productTitleText}
        description={metaDescription}
        path={bareProductPath}
        imageUrl={product.heroImageUrl}
        structuredData={[
          // WebPage (not Product): store-finder landings have no price/offers/
          // reviews. Incomplete Product schema fails GSC Product snippets.
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: productTitleText,
            url: productUrl,
            description: metaDescription,
            image: product.heroImageUrl,
            isPartOf: {
              "@type": "WebSite",
              name: SITE_NAME,
              url: SITE_URL + "/",
            },
            about: {
              "@type": "Thing",
              name: productTitleText,
              description: metaDescription,
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: t("common.home"),
                item: homeUrl,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: productTitleText,
                item: productUrl,
              },
            ],
          },
        ]}
      />
      <Header />

      <div className="min-h-screen bg-slate-900 text-slate-300 font-sans antialiased">
        <header
          className="w-full min-h-[64vh] md:min-h-[70vh] lg:min-h-[76vh] border-b border-slate-800 pb-8 md:pb-12"
          style={heroStyle}
        >
          <div className="max-w-6xl mx-auto pt-10 md:pt-16 pl-6">
            <span
              className={`inline-block text-white text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-widest ${accentClasses.badge}`}
            >
              {product.badgeText}
            </span>
            <h1 className="text-[30px] md:text-[48px] font-black text-white mb-4 leading-tight drop-shadow-lg flex flex-col gap-y-4">
              <span>{product.h1Line1}</span>

              <span className={accentClasses.accent}>{product.h1Accent}</span>
            </h1>
            <h2 className="text-base md:text-xl text-slate-100 max-w-2xl font-light drop-shadow-md">
              {product.subtitleLine1}
              <br />
              {product.subtitleLine2}
            </h2>
          </div>
          {product.images.length > 0 ? (
            <>
              <MobileThumbPreviewGallery
                images={product.images}
                alt={product.h1Accent}
                thumbBorderClass={accentClasses.thumbBorder}
              />
              <div className="hidden md:grid lg:w-[70%] w-full px-4 mx-auto grid-cols-3 gap-4 lg:mt-10 md:mt-11 mt-6">
                {product.images.map((image, index) => (
                  <div
                    key={image}
                    className="product-info-gallery-item flex items-center justify-center"
                    style={{ animationDelay: `${index * 0.12}s` }}
                  >
                    <img
                      src={image}
                      alt={product.h1Accent}
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                      className="product-info-gallery-image w-full h-auto max-h-[30vh] lg:max-h-[280px] object-contain cursor-pointer select-none [-webkit-user-drag:none]"
                    />
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </header>

        <main className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-3 gap-12">
          <div className="md:col-span-2 space-y-10">
            <section>
              <h3 className="text-xl font-bold text-white mb-4 border-b border-slate-700 pb-2">
                {product.sectionTitle}
              </h3>
              <p className="text-sm leading-relaxed text-slate-400">
                {product.sectionBody}
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-white mb-4 border-b border-slate-700 pb-2">
                {t("product.specifications")}
              </h3>
              <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700">
                <ul className="space-y-3 text-slate-300">
                  {product.specs.map((row, i) => (
                    <li
                      key={`${product.id}-spec-${i}`}
                      className={
                        i < product.specs.length - 1
                          ? "flex border-b border-slate-700/50 pb-2"
                          : "flex"
                      }
                    >
                      <span className="w-24 font-semibold text-slate-400 shrink-0 text-sm">
                        {row.label}
                      </span>
                      <span className="text-sm">{row.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </div>

          <aside className="md:col-span-1">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-2xl sticky top-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {product.asideTitle}
              </h3>
              <p className="text-sm text-slate-400 mb-6">{product.asideBody}</p>

              <form
                className="space-y-4"
                autoComplete="off"
                onSubmit={handleLocationSubmit}
                noValidate
              >
                <div>
                  <label
                    htmlFor={locationInputId}
                    className="block text-xs font-semibold text-slate-400 mb-1"
                  >
                    {t("product.yourLocation")}
                  </label>
                  <input
                    id={locationInputId}
                    name="tcg-store-region-query"
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      if (locationError) setLocationError("");
                    }}
                    type="text"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    data-lpignore="true"
                    data-1p-ignore
                    placeholder={t("product.locationPlaceholder")}
                    aria-invalid={locationError ? true : undefined}
                    aria-describedby={
                      locationError ? `${locationInputId}-error` : undefined
                    }
                    className={`w-full bg-slate-900 border rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-1 transition ${
                      locationError
                        ? "border-red-500 focus:ring-red-500"
                        : `border-slate-600 ${accentClasses.inputFocus}`
                    }`}
                  />
                  {locationError ? (
                    <p
                      id={`${locationInputId}-error`}
                      className="mt-2 text-sm text-red-400"
                      role="alert"
                    >
                      {locationError}
                    </p>
                  ) : null}
                </div>
                <button
                  type="submit"
                  className={`w-full text-white font-bold py-3 px-4 rounded-lg transition duration-200 ease-in-out transform hover:-translate-y-1 flex justify-center items-center gap-2 ${accentClasses.cta}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {product.ctaText}
                </button>
                <p className="text-xs text-center text-slate-400 mt-4">
                  {product.filterHint}
                </p>
              </form>
            </div>
          </aside>
        </main>
      </div>
      <Footer />
    </>
  );
}
